/**
 * AdminTopbar — Aurora Glass top bar for admin panel.
 *
 * Left:  Page title (from route)
 * Right: Search · Dark toggle · Bell · Language · UserPill
 */
import { useState, useRef, useEffect } from 'react'
import { useTranslation }              from 'react-i18next'
import { useLocation }                 from 'react-router-dom'
import { useAuth }                     from '@/context/AuthContext'
import { useTheme }                    from '@/context/ThemeContext'
import { setLocale, SUPPORTED_LOCALES }from '@/i18n/i18n'
import { Sun, Moon, Bell, Search }     from 'lucide-react'
import { UserPill, Pill }              from '@/components/aurora'

const ROUTE_TITLES = {
  '/dashboard':      'nav.dashboard',
  '/questions':      'nav.questions',
  '/question-banks': 'nav.questionBanks',
  '/topics':         'nav.topics',
  '/subjects':       'nav.subjects',
  '/exams':          'nav.exams',
  '/analytics':      'nav.analytics',
  '/groups':         'nav.groups',
  '/schedule':       'nav.schedule',
  '/materials':      'nav.materials',
  '/forum':          'nav.forum',
  '/users':          'nav.users',
  '/settings':       'nav.settings',
  '/grading-criteria': 'nav.gradingCriteria',
  '/ai-logs':          'nav.aiLogs',
}

const LOCALE_LABELS = { uz: "Oʻzbekcha", ru: 'Русский', en: 'English' }
const LOCALE_FLAGS  = { uz: '🇺🇿', ru: '🇷🇺', en: '🇬🇧' }

export default function Topbar() {
  const { t, i18n }            = useTranslation()
  const { user }               = useAuth()
  const { theme, toggleTheme } = useTheme()
  const isDark = theme === 'dark'
  const location               = useLocation()

  const [langOpen, setLangOpen] = useState(false)
  const langRef                 = useRef(null)

  const currentLng = i18n.language
  const routeKey   = Object.keys(ROUTE_TITLES).find((k) => location.pathname.startsWith(k))
  const pageTitle  = routeKey ? t(ROUTE_TITLES[routeKey]) : '5mi.uz'

  useEffect(() => {
    function handler(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <header className="
      h-14 flex-shrink-0 flex items-center justify-between
      bg-white/[0.04] border-b border-white/10 backdrop-blur-xl
      px-5 gap-4 z-10
    ">
      {/* ── Left: Title + search ──────────────────────────── */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <h1 className="text-base font-semibold text-white tracking-tight truncate m-0">
          {pageTitle}
        </h1>

        <div className="hidden md:flex items-center gap-2 flex-1 max-w-xs
          bg-white/[0.06] border border-white/10 rounded-xl px-3 py-1.5">
          <Search size={14} className="text-white/40 flex-shrink-0" />
          <input
            type="text"
            placeholder={t('common.search', 'Qidirish…')}
            className="bg-transparent text-sm text-white placeholder:text-white/35 outline-none w-full"
          />
        </div>
      </div>

      {/* ── Right controls ───────────────────────────────── */}
      <div className="flex items-center gap-1.5 flex-shrink-0">

        {/* Theme toggle */}
        <Pill
          iconOnly
          onClick={toggleTheme}
          title={isDark ? 'Yorugʻ rejim' : 'Qorongʻu rejim'}
          aria-label="Toggle theme"
        >
          {isDark ? <Sun size={14} strokeWidth={2} /> : <Moon size={14} strokeWidth={2} />}
        </Pill>

        {/* Notifications bell */}
        <Pill iconOnly title="Bildirishnomalar" aria-label="Notifications" className="relative">
          <Bell size={14} strokeWidth={2} />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-violet-400 ring-2 ring-[#0A0717]" />
        </Pill>

        {/* Language switcher */}
        <div ref={langRef} className="relative">
          <Pill onClick={() => setLangOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={langOpen}>
            <span className="text-base leading-none">{LOCALE_FLAGS[currentLng]}</span>
            <span className="hidden sm:inline text-xs">{currentLng.toUpperCase()}</span>
          </Pill>

          {langOpen && (
            <div
              role="listbox"
              className="absolute right-0 top-full mt-1.5 w-44
                rounded-2xl overflow-hidden z-50 py-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}
            >
              {SUPPORTED_LOCALES.map((lng) => (
                <button
                  key={lng}
                  role="option"
                  aria-selected={lng === currentLng}
                  onClick={() => { setLocale(lng); setLangOpen(false) }}
                  className={[
                    'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left',
                    lng === currentLng
                      ? 'bg-violet-500/20 text-violet-300 font-semibold'
                      : 'text-white/72 hover:bg-white/[0.06] hover:text-white',
                  ].join(' ')}
                >
                  <span className="text-base">{LOCALE_FLAGS[lng]}</span>
                  <span>{LOCALE_LABELS[lng]}</span>
                  {lng === currentLng && <span className="ml-auto text-violet-400 text-xs">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User pill */}
        {user && <UserPill user={user} as="div" />}
      </div>
    </header>
  )
}
